// View-adjustment presets (F-16.4) and team preset files (.acpreset, D-12, docs/04-data-model.md).

import type { Levels, ViewAdjustmentValues } from './types';
import { LEVELS_IDENTITY } from './color';

export interface AdjustPreset {
  id: string;
  /** i18n key for built-ins; custom presets carry a plain name. */
  labelKey?: string;
  name?: string;
  values: ViewAdjustmentValues;
}

const L = (inBlack: number, inWhite: number, mid = 1): Levels => ({ ...LEVELS_IDENTITY, inBlack, inWhite, mid });

export const BUILTIN_PRESETS: AdjustPreset[] = [
  { id: 'shadows', labelKey: 'preset.shadows', values: { exposureStops: 1.5, gamma: 1.4, levels: { ...LEVELS_IDENTITY } } },
  { id: 'highlights', labelKey: 'preset.highlights', values: { exposureStops: -1.5, gamma: 0.8, levels: { ...LEVELS_IDENTITY } } },
  // Banding: stretch the mid-tones hard so steps between adjacent codes become visible.
  { id: 'banding', labelKey: 'preset.banding', values: { exposureStops: 0, gamma: 1, levels: L(90 / 255, 170 / 255) } },
];

export const PRESET_FILE_EXT = '.acpreset';
export const PRESET_SCHEMA = '1.0';

export interface TeamPresetFile {
  kind: 'aicompare-preset';
  schemaVersion: string;
  name: string;
  adjustPresets: AdjustPreset[];
  shortcuts?: Record<string, string>;
  export?: { internalBitrateMbps?: number; clientLabels?: { before: string; after: string } };
}

export function makePresetFile(name: string, adjustPresets: AdjustPreset[], shortcuts?: Record<string, string>): TeamPresetFile {
  return { kind: 'aicompare-preset', schemaVersion: PRESET_SCHEMA, name, adjustPresets, shortcuts };
}

export function parsePresetFile(text: string): TeamPresetFile | null {
  try {
    const o = JSON.parse(text) as Partial<TeamPresetFile>;
    if (o.kind !== 'aicompare-preset' || !Array.isArray(o.adjustPresets)) return null;
    const presets = o.adjustPresets.filter(
      (p): p is AdjustPreset => !!p && typeof p.id === 'string' && !!p.values && typeof p.values.exposureStops === 'number' && !!p.values.levels,
    );
    return {
      kind: 'aicompare-preset',
      schemaVersion: String(o.schemaVersion ?? PRESET_SCHEMA),
      name: String(o.name ?? ''),
      adjustPresets: presets,
      shortcuts: o.shortcuts && typeof o.shortcuts === 'object' ? o.shortcuts : undefined,
      export: o.export,
    };
  } catch {
    return null;
  }
}
